import React from 'react';
import styles from '@dazn/lp-web/src/components/ContentTiers/content-tiers.module.css';
import {
    CardHeaderDetails,
    ContentTierBadgeColor,
    IContentTier
} from '@dazn/lp-web/src/types/contentTiers.types';
import classnames from 'classnames';
import Markdown from 'markdown-to-jsx';

import {IOffer} from '@dazn/lp-web/src/types/offers.types';
import {getCardHeaderDetails} from '@dazn/lp-web/src/components/ContentTiers/contentTiersUtils';
import {checkIfFranceHalfSeasonEnabled} from '@dazn/lp-web/src/features';
import {useMedia} from '../../hooks/useMedia';
import {StylingWrapper} from './TierContentWrapper';
import {ChevronToggle} from './ChevronToggle';

interface ICardHeaderComponentProps {
    contentTier: IContentTier;
    offerDetail: IOffer | null;
    comparedOffer?: IOffer | null;
    showImage: boolean;
    bestValueBadgeShown: boolean;
    billingPlan?: string;
    planSelectorShown: boolean;
    showPlanUI: boolean;
    modifiedDescription?: string;
    isEyebrowAdded?: string | boolean;
    remainingContentTiers?: boolean;
    version?: string;
    onV3ChevronClick?: (newIndex: number) => void;
    handleScrollToElement?: (elementId: string) => void;
    v3ClickedChevron?: number;
    index?: number;
}

const PriceDetails = (details: CardHeaderDetails) => {
    const cleanText = (text?: string) => text?.replace('**', '') ?? '';

    const priceFrom = cleanText(details.priceFrom);
    const priceLabel = cleanText(details.priceLabel);
    const shouldShowBiggerPrice = !priceFrom && !priceLabel;
    return (
        <div
            className={classnames(styles.priceDetailsWrapper, {
                [styles.topMarginForPriceDetailsWrapper]: shouldShowBiggerPrice
            })}
        >
            {priceFrom && (
                <div className={styles.priceAffixes}>{priceFrom}</div>
            )}
            <div
                className={classnames(styles.priceDetails, {
                    [styles.biggerFont]: shouldShowBiggerPrice
                })}
            >
                {details.price}
            </div>
            {priceLabel && (
                <div className={styles.priceAffixes}>{priceLabel}</div>
            )}
        </div>
    );
};

const CardHeaderComponent: React.FC<ICardHeaderComponentProps> = ({
    contentTier,
    offerDetail,
    showImage = true,
    bestValueBadgeShown,
    billingPlan,
    planSelectorShown,
    showPlanUI,
    // comparedOffer,
    modifiedDescription,
    remainingContentTiers = false,
    version = 'v1',
    onV3ChevronClick,
    v3ClickedChevron,
    index,
    handleScrollToElement
}): JSX.Element => {
    const {
        title,
        badgeColor,
        bestValueBadgeText,
        bestValueBadgeTextInstallment,
        bestValueBadgeTextAnnual,
        bestValueBadgeTextMonthly
    } = contentTier;

    const getBadgeText = () => {
        if (planSelectorShown) {
            switch (billingPlan) {
                case 'Annual':
                    return bestValueBadgeTextAnnual;
                case 'Instalments':
                    return bestValueBadgeTextInstallment;
                case 'Month':
                    return bestValueBadgeTextMonthly;
                default:
                    return '';
            }
        }
        return bestValueBadgeText || '';
    };
    const {isMobile} = useMedia();
    const bestValueBadgeTextModified = getBadgeText();
    const details = getCardHeaderDetails(offerDetail, contentTier, billingPlan);
    const isFranceHalfSeasonEnabled =
        checkIfFranceHalfSeasonEnabled() &&
        ['', 'DAZN'].includes(offerDetail?.ProductGroup ?? '');
    // const comparedPrice = getCardHeaderDetails(
    //     comparedOffer,
    //     contentTier,
    //     billingPlan
    // );
    const isShrinked = index === v3ClickedChevron ? false : true;
    const shouldApplyForVersion3 = version === 'v3' && isMobile;

    if (showPlanUI) {
        return (
            <div
                className={classnames(styles.cardPriceContentNewTier, {
                    [styles.removePadding]: !remainingContentTiers
                })}
            >
                {title && (
                    <>
                        <h5
                            {...(!remainingContentTiers && {
                                'data-sync-height': 'CardTitle'
                            })}
                            className={classnames(styles.cardTitleNewUI, {
                                [styles.justifiedSpace]: details.showBadge
                            })}
                        >
                            <Markdown as="span">{title}</Markdown>
                            {details.showBadge && (
                                <Markdown className={styles.cardBadgeNewUI}>
                                    {details.badgeText}
                                </Markdown>
                            )}
                        </h5>
                    </>
                )}

                {/* {isPayopsOfferDetails() &&
                    comparedPrice &&
                    comparedPrice.price && (
                        <Markdown className={styles.cardDiscountNewTier}>
                            {localisePrice(
                                Number(
                                    comparedPriceValue?.[entitlementSetId]?.[
                                        billingPeriod
                                    ]?.OriginalPrice
                                )
                            )}
                        </Markdown>
                    )} */}

                {offerDetail && (
                    <div
                        className={styles.cardNewPriceformat}
                        {...(!isMobile &&
                            !remainingContentTiers && {
                                'data-sync-height': 'CardPricePoint'
                            })}
                    >
                        {details.showDiscount && (
                            <Markdown
                                className={classnames(
                                    styles.cardPriceNewTierDiscount,
                                    {
                                        [styles.seeMorePlansPrice]:
                                            remainingContentTiers
                                    }
                                )}
                            >
                                {details.price.toString()}
                            </Markdown>
                        )}
                        {details.showDiscount && details.showPrice && (
                            <Markdown
                                className={classnames(styles.cardPriceNewTier, {
                                    [styles.seeMorePlansPrice]:
                                        remainingContentTiers
                                })}
                            >
                                {details.discountAmount.toString()}
                            </Markdown>
                        )}
                        {!details.showDiscount &&
                        details.showPrice &&
                        isFranceHalfSeasonEnabled ? (
                            <PriceDetails {...details} />
                        ) : (
                            <Markdown
                                className={classnames(styles.cardPriceNewTier, {
                                    [styles.seeMorePlansPrice]:
                                        remainingContentTiers
                                })}
                            >
                                {details.price.toString()}
                            </Markdown>
                        )}
                        <small
                            className={classnames(
                                styles.cardPriceLabelNewTier,
                                {
                                    [styles.reduceTop]:
                                        !remainingContentTiers &&
                                        !isFranceHalfSeasonEnabled
                                }
                            )}
                        >
                            {modifiedDescription}
                        </small>
                    </div>
                )}
            </div>
        );
    }
    return (
        <div
            className={classnames(styles.cardMetaData)}
            {...(!isMobile &&
                !remainingContentTiers && {
                    'data-sync-height': 'TierCardMetaData'
                })}
        >
            {bestValueBadgeShown && bestValueBadgeTextModified && (
                <p
                    className={styles.cardBestValueBadge}
                    data-sync-height="BestValueBadge"
                >
                    <Markdown as="span">{bestValueBadgeTextModified}</Markdown>
                </p>
            )}
            {bestValueBadgeShown && !bestValueBadgeTextModified && (
                <div
                    className={styles.emptyCardBestValueBadge}
                    data-sync-height="BestValueBadge"
                ></div>
            )}
            {title && (
                <StylingWrapper
                    condition={shouldApplyForVersion3}
                    wrapperClassName={styles.v3TitleWrapper}
                >
                    <h5
                        data-sync-height="CardTitle"
                        className={classnames(styles.cardTitle, {
                            [styles.noImage]: !showImage,
                            [styles.bestValueBadgeShown]: bestValueBadgeShown
                        })}
                    >
                        <Markdown as="span">{title}</Markdown>
                    </h5>
                    {shouldApplyForVersion3 && (
                        <ChevronToggle
                            version={version}
                            onV3ChevronClick={onV3ChevronClick}
                            handleScrollToElement={handleScrollToElement}
                            v3ClickedChevron={v3ClickedChevron}
                            index={index as number}
                            isShrinked={isShrinked}
                        />
                    )}
                </StylingWrapper>
            )}
            <div
                className={styles.cardPriceContainer}
                data-sync-height="PriceContainer"
            >
                {details.showBadge && (
                    <Markdown
                        className={classnames(styles.cardBadge, {
                            [styles.cardBadgeGold]:
                                badgeColor === ContentTierBadgeColor.GOLD
                        })}
                    >
                        {details.badgeText}
                    </Markdown>
                )}
                <div className={styles.cardPriceContent}>
                    {details.hasFreeTrial && (
                        <p className={classnames(styles.freeTrialBadge)}>
                            {details.freeTrialText}
                        </p>
                    )}
                    {details.showPrice && details.priceFrom && (
                        <Markdown className={styles.cardPriceLabel}>
                            {details.priceFrom}
                        </Markdown>
                    )}
                    {details.showDiscount && (
                        <Markdown className={styles.cardDiscount}>
                            {details.price.toString()}
                        </Markdown>
                    )}
                    {details.showDiscount && details.showPrice && (
                        <Markdown className={styles.cardPrice}>
                            {details.discountAmount.toString()}
                        </Markdown>
                    )}
                    {!details.showDiscount && details.showPrice && (
                        <Markdown className={styles.cardPrice}>
                            {details.price.toString()}
                        </Markdown>
                    )}
                    {details.priceLabel && (
                        <Markdown className={styles.cardPriceLabel}>
                            {details.priceLabel}
                        </Markdown>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CardHeaderComponent;
